import React, { Suspense } from "react";
import "./App.css";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import FullPageLoader from "./components/loaders/FullPageLoader";
import AddonGate from "./components/addons/AddonGate";
import GlobalChatbot from "./components/GlobalChatbot";
import CookieConsentBanner from "./components/CookieConsentBanner";
import TopBanners from "./components/TopBanners";
import { DemoGuardProvider } from "./contexts/DemoGuardContext";

// `lazyRetry` wraps React.lazy with a one-shot reload-on-chunk-failure so
// stale bundles after a Vercel deploy don't leave users stranded on a
// broken page (see src/utils/lazyRetry.js for the why).
import lazyRetry from "./utils/lazyRetry";
const lazy = lazyRetry;

import Login from "./pages/Login";

// Route guards
import ProtectedRoute from "./utils/ProtectedRoute";

// Lazy loaded pages
const PaymentPage = lazy(() => import("./pages/Payment"));
const LandingPage = lazy(() => import("./pages/Landing"));
const PrivacyPolicy = lazy(() => import("./pages/Privacy-Policy"));
const Status = lazy(() => import("./pages/Status"));
const CookiesPolicy = lazy(() => import("./pages/Cookies-Policy"));
const TermsandConditionsPolicy = lazy(() => import("./pages/Terms-and-Conditions-Policy"));
const RefundCancellationPolicy = lazy(() => import("./pages/Refund-Cancellation-Policy"));
const Changelog = lazy(() => import("./pages/Changelog"));
const Pricing = lazy(() => import("./pages/Pricing"));
const RealEstate = lazy(() => import("./pages/Pricepackages/RealEstate"));
const Wholesalers = lazy(() => import("./pages/Pricepackages/Wholesalers"));
const Probate = lazy(() => import("./pages/Pricepackages/Probate"));
const HomeFlippers = lazy(() => import("./pages/Pricepackages/HomeFlippers"));
const PricingSummary = lazy(() => import("./pages/PricingSummary"));
const AffiliateDashboard = lazy(() => import("./pages/AffiliatedDashboard"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const SMSOptOut = lazy(() => import("./pages/SMSOptOut"));
const SMSOptIn = lazy(() => import("./pages/SMSOptIn"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Referral = lazy(() => import("./pages/Referral"));
const PublicFeedbackForm = lazy(() => import("./pages/Public/PublicFeedbackForm"));
const ESignPage = lazy(() => import("./pages/Public/ESignPage"));

// Team Portal (magic-link access for team members)
const TeamLoginPage = lazy(() => import("./pages/TeamLoginPage"));
const TeamPortalPage = lazy(() => import("./pages/TeamPortalPage"));

// Admin
const AdminLogin = lazy(() => import("./pages/Admin/Login"));
const AdminDashboard = lazy(() => import("./pages/Admin/Dashboard"));
const AdminAnalytics = lazy(() => import("./pages/Admin/Analytics"));
const AdminDemos = lazy(() => import("./pages/Admin/Demos"));
const AdminEmails = lazy(() => import("./pages/Admin/Emails"));
const AdminPayments = lazy(() => import("./pages/Admin/Payments"));
const AdminAffiliates = lazy(() => import("./pages/Admin/Affiliates"));
const AdminNewsLetters = lazy(() => import("./pages/Admin/NewsLetters"));
const AdminCalendar = lazy(() => import("./pages/Admin/Calendar"));
const AccountLookUp = lazy(() => import("./pages/Admin/AccountLookUp"));
const AdminActivities = lazy(() => import("./pages/Admin/Activities"));
const AdminTickets = lazy(() => import("./pages/Admin/Tickets"));
const AdminGoals = lazy(() => import("./pages/Admin/Goals"));
const AdminLeads = lazy(() => import("./pages/Admin/Leads"));
const AdminFollowUps = lazy(() => import("./pages/Admin/FollowUps"));
const AdminTasks = lazy(() => import("./pages/Admin/Tasks"));
const AdminTexting = lazy(() => import("./pages/Admin/Texting"));
const AdminUserManagement = lazy(() => import("./pages/Admin/UserManagement"));
const AdminBusinessInsights = lazy(() => import("./pages/Admin/BusinessInsights"));
const AdminAuditLog = lazy(() => import("./pages/Admin/AuditLog"));
const AdminSuspiciousActivity = lazy(() => import("./pages/Admin/SuspiciousActivity"));
const AdminSecurity = lazy(() => import("./pages/Admin/Security"));
const AdminListFormatting = lazy(() => import("./pages/Admin/ListFormatting"));
const AdminBroadcastEmail = lazy(() => import("./pages/Admin/BroadcastEmail"));
const AdminWebhookEvents = lazy(() => import("./pages/Admin/WebhookEvents"));

// Real Estate
const RealDashboard = lazy(() => import("./components/RealEstate/Layout"));
const RealIndex = lazy(() => import("./pages/RealEstate/RealEstate"));
const DashboardRouter = lazy(() => import("./pages/RealEstate/DashboardRouter"));
const RealPageNotFound = lazy(() => import("./pages/RealEstate/NotFound"));
const RealPricing = lazy(() => import("./components/RealEstate/RealPricing"));
const RealCients = lazy(() => import("./pages/RealEstate/Clients"));
const RealProperties = lazy(() => import("./pages/RealEstate/AllProperties"));
const RealPropertyDetails = lazy(() => import("./pages/RealEstate/PropertyDetailsNew"));
const RealCalendar = lazy(() => import("./pages/RealEstate/Calender"));
const RealDocument = lazy(() => import("./pages/RealEstate/DocumentDetailPages"));
const RealAnalytics = lazy(() => import("./pages/RealEstate/Analytics"));
const RealLibrary = lazy(() => import("./pages/RealEstate/LibraryPage"));
const RealSettings = lazy(() => import("./pages/RealEstate/Settings"));
const RealHelpCenter = lazy(() => import("./pages/RealEstate/HelpCenter"));
const RealMessaging = lazy(() => import("./pages/RealEstate/Messaging"));
// Additional Real Estate pages
const RealMarketingTools = lazy(() => import("./pages/RealEstate/MarketingTools/index"));
const RealOpenHouseToolkit = lazy(() => import("./pages/RealEstate/OpenHouseToolkitEnhanced"));
const WebsiteBuilder = lazy(() => import("./pages/RealEstate/WebsiteBuilder"));
const RealPipeline = lazy(() => import("./pages/RealEstate/Pipeline"));
const RealClientImport = lazy(() => import("./pages/RealEstate/ClientImport"));
const CRMImportWizard = lazy(() => import("./pages/RealEstate/CRMImportWizard"));
const CRMImportHistory = lazy(() => import("./pages/RealEstate/CRMImportHistory"));
const EmailSignatureBuilder = lazy(() => import("./pages/RealEstate/EmailSignatureBuilder"));
const CSVImporter = lazy(() => import("./pages/RealEstate/CSVImporter"));
const RealQuickReferral = lazy(() => import("./pages/RealEstate/QuickReferralTool"));
const RealNewLearning = lazy(() => import("./pages/RealEstate/NewLearning"));
const RealClientDetail = lazy(() => import("./pages/RealEstate/ClientDetail"));
const RealClientTimeline = lazy(() => import("./pages/RealEstate/ClientTimeline"));
const RealClientStatus = lazy(() => import("./pages/RealEstate/ClientStatus"));
const RealVoiceCalls = lazy(() => import("./pages/RealEstate/VoiceCalls"));
const RealBasicDialer = lazy(() => import("./pages/RealEstate/BasicDialer"));
const RealEmailInbox = lazy(() => import("./pages/RealEstate/EmailInbox"));
const RealEmailTemplateGallery = lazy(() => import("./pages/RealEstate/EmailTemplateGallery"));
const RealMyFavorites = lazy(() => import("./pages/RealEstate/MyFavorites"));
const RealClientEngagement = lazy(() => import("./pages/RealEstate/PowerPlan/ClientEngagement"));
const RealTaskManagement = lazy(() => import("./pages/RealEstate/TaskManagement"));
const RealTransactionManagement = lazy(() => import("./pages/RealEstate/TransactionManagement"));
const RealListingLifecycle = lazy(() => import("./pages/RealEstate/ListingLifecycle"));
const RealMyListings = lazy(() => import("./pages/RealEstate/MyListings"));
const RealGoogleContactsSync = lazy(() => import("./pages/RealEstate/GoogleContactsSync"));
const RealConnectedAccounts = lazy(() => import("./pages/RealEstate/ConnectedAccounts"));
const RealCommunicationTracking = lazy(() => import("./pages/RealEstate/CommunicationTracking"));

// Pro Plan Pages
const RealDealAnalyzer = lazy(() => import("./pages/RealEstate/DealAnalyzer"));
const RealNeighborhoodFarms = lazy(() => import("./pages/RealEstate/NeighborhoodFarms"));
const RealSOITracker = lazy(() => import("./pages/RealEstate/SOITracker"));
const RealShowingFeedback = lazy(() => import("./pages/RealEstate/ShowingFeedback"));
const RealFollowUpSequences = lazy(() => import("./pages/RealEstate/FollowUpSequences"));
const RealVendorScorecard = lazy(() => import("./pages/RealEstate/VendorScorecard"));
const RealAgentKPIDashboard = lazy(() => import("./pages/RealEstate/AgentKPIDashboard"));
const RealProfitLossTracker = lazy(() => import("./pages/RealEstate/ProfitLossTracker"));
const RealGoalTracker = lazy(() => import("./pages/RealEstate/GoalTracker"));

// Starter Plan Pages - New Features
const RealListingChecklist = lazy(() => import("./pages/RealEstate/ListingChecklist"));
const RealBuyerPreferences = lazy(() => import("./pages/RealEstate/BuyerPreferencesTracker"));

// Power Plan Pages - New Features
const RealClosingCountdown = lazy(() => import("./pages/RealEstate/ClosingCountdown"));
const RealShowingScheduler = lazy(() => import("./pages/RealEstate/ShowingScheduler"));

// Power Plan Pages
const RealESignatureHub = lazy(() => import("./pages/RealEstate/PowerPlan/ESignatureHub"));
const RealLeadCapture = lazy(() => import("./pages/RealEstate/PowerPlan/LeadCapture"));
const RealLeadInbox = lazy(() => import("./pages/RealEstate/LeadInbox"));
const RealLeadScoring = lazy(() => import("./pages/RealEstate/LeadScoring"));
const RealSpeedToLead = lazy(() => import("./pages/RealEstate/SpeedToLeadSettings"));
const RealBulkSend = lazy(() => import("./pages/RealEstate/BulkSendPage"));
const RealLeadRouting = lazy(() => import("./pages/RealEstate/LeadRoutingSettings"));
const RealMortgageCalc = lazy(() => import("./pages/RealEstate/MortgageCalculator"));
const RealSellerNetSheet = lazy(() => import("./pages/RealEstate/SellerNetSheet"));
const RealAffordabilityCalc = lazy(() => import("./pages/RealEstate/AffordabilityCalculator"));
const RealQRCodeGen = lazy(() => import("./pages/RealEstate/QRCodeGenerator"));
const RealMortgageRates = lazy(() => import("./pages/RealEstate/MortgageRates"));
const RealMarketingDashboard = lazy(() => import("./pages/RealEstate/PowerPlan/MarketingDashboard"));
const RealAutoDripCampaigns = lazy(() => import("./pages/RealEstate/PowerPlan/AutoDripCampaigns"));
const RealCommissionTracker = lazy(() => import("./pages/RealEstate/CommissionTrackerPage"));
const RealReviewRequests = lazy(() => import("./pages/RealEstate/ReviewRequestsPage"));
const RealClientAnniversaries = lazy(() => import("./pages/RealEstate/ClientAnniversariesPage"));
const RealVendorDirectory = lazy(() => import("./pages/RealEstate/VendorDirectoryPage"));

// Wholesale Power Plan Pages
const WholesaleDealTracker = lazy(() => import("./pages/RealEstate/DealTrackerPage"));
const WholesaleBuyerFollowup = lazy(() => import("./pages/RealEstate/BuyerFollowupPage"));
const WholesaleDispoChecklist = lazy(() => import("./pages/RealEstate/DispositionChecklistPage"));
const WholesaleSkipTraceManager = lazy(() => import("./pages/RealEstate/SkipTraceManagerPage"));

// Wholesale Pro Plan Pages
const WholesaleKPIDashboard = lazy(() => import("./pages/RealEstate/KPIDashboardPage"));
const WholesaleVATasks = lazy(() => import("./pages/RealEstate/VATaskManagerPage"));
const WholesaleInvestorPortal = lazy(() => import("./pages/RealEstate/InvestorPortalPage"));
const WholesaleTeamManagement = lazy(() => import("./pages/RealEstate/TeamManagementPage"));
const WholesaleDispoManagement = lazy(() => import("./pages/RealEstate/DispoManagementPage"));
const WholesaleAnalytics = lazy(() => import("./pages/RealEstate/Wholesale/WholesaleAnalytics"));
const ProbateAnalytics = lazy(() => import("./pages/RealEstate/Probate/ProbateAnalytics"));

// Additional Wholesale Pages
const WholesaleRehabEstimator = lazy(() => import("./pages/RealEstate/RehabEstimatorPage"));
const WholesaleContractsManager = lazy(() => import("./pages/RealEstate/ContractsManagerPage"));
const WholesaleESignatureHub = lazy(() => import("./pages/RealEstate/Wholesale/WholesaleESignatureHub"));
const WholesaleGoalTracker = lazy(() => import("./pages/RealEstate/Wholesale/WholesaleGoalTracker"));

// Wholesale CRM Pages
const WholesaleSellers = lazy(() => import("./pages/RealEstate/Wholesale/Sellers"));
const WholesaleBuyers = lazy(() => import("./pages/RealEstate/Wholesale/Buyers"));
const WholesaleDeals = lazy(() => import("./pages/RealEstate/Wholesale/Deals"));
const WholesaleListStacking = lazy(() => import("./pages/Wholesale/ListStacking"));
const WholesaleSellerPipeline = lazy(() => import("./pages/Wholesale/SellerPipeline"));
const WholesaleBuyerBlast = lazy(() => import("./pages/Wholesale/BuyerBlast"));
const ProbatePipeline = lazy(() => import("./pages/Probate/ProbatePipeline"));
const ProbateLetterTemplates = lazy(() => import("./pages/Probate/ProbateLetterTemplates"));
const EstateAssetInventory = lazy(() => import("./pages/Probate/EstateAssetInventory"));
const ProbateMilestones = lazy(() => import("./pages/Probate/ProbateMilestones"));
const FlipPipeline = lazy(() => import("./pages/Flipper/FlipPipeline"));
const RehabPunchList = lazy(() => import("./pages/Flipper/RehabPunchList"));
const FlipProfitCalc = lazy(() => import("./pages/Flipper/FlipProfitCalculator"));
const RentalROICalc = lazy(() => import("./pages/Flipper/RentalROICalculator"));
const WholesaleDrivingForDollars = lazy(() => import("./pages/RealEstate/Wholesale/DrivingForDollars"));
const WholesaleOfferCalculator = lazy(() => import("./pages/RealEstate/Wholesale/OfferCalculator"));
const WholesaleProfitLossTracker = lazy(() => import("./pages/RealEstate/Wholesale/ProfitLossTracker"));

// Probate CRM Pages
const ProbateCases = lazy(() => import("./pages/RealEstate/Probate/Cases"));
const ProbateHeirs = lazy(() => import("./pages/RealEstate/Probate/Heirs"));
const ProbateCourtCalendar = lazy(() => import("./pages/RealEstate/Probate/CourtCalendar"));
const ProbateCaseChecklist = lazy(() => import("./pages/RealEstate/Probate/CaseChecklist"));
const ProbateHeirFinder = lazy(() => import("./pages/RealEstate/Probate/HeirFinder"));
const ProbateCaseTimeline = lazy(() => import("./pages/RealEstate/Probate/CaseTimeline"));
const ProbateCourtFilingTracker = lazy(() => import("./pages/RealEstate/Probate/CourtFilingTracker"));
const ProbateAttorneyNetwork = lazy(() => import("./pages/RealEstate/Probate/AttorneyNetwork"));
const ProbateEstateValuation = lazy(() => import("./pages/RealEstate/Probate/EstateValuation"));
const ProbateKPIs = lazy(() => import("./pages/RealEstate/Probate/ProbateKPIs"));
const ProbateProfitLoss = lazy(() => import("./pages/RealEstate/Probate/ProbateProfitLoss"));
const ProbateGoalTracker = lazy(() => import("./pages/RealEstate/Probate/ProbateGoalTracker"));
const ProbateCountyAnalytics = lazy(() => import("./pages/RealEstate/Probate/ProbateCountyAnalytics"));
const ProbateReferralNetwork = lazy(() => import("./pages/RealEstate/Probate/ProbateReferralNetwork"));
const ProbateSkipTrace = lazy(() => import("./pages/RealEstate/Probate/ProbateSkipTrace"));
const FlipperAnalytics = lazy(() => import("./pages/RealEstate/Flippers/FlipperAnalytics"));

// Flipper CRM Pages
const FlipperProjects = lazy(() => import("./pages/RealEstate/Flippers/Projects"));
const FlipperContractors = lazy(() => import("./pages/RealEstate/Flippers/Contractors"));
const FlipperBudgetTracker = lazy(() => import("./pages/RealEstate/Flippers/BudgetTracker"));
const FlipperHoldingCosts = lazy(() => import("./pages/RealEstate/Flippers/HoldingCosts"));
const FlipperARVCalculator = lazy(() => import("./pages/RealEstate/Flippers/ARVCalculator"));
const FlipperOfferCalculator = lazy(() => import("./pages/RealEstate/Flippers/OfferCalculator"));
const FlipperProjectTimeline = lazy(() => import("./pages/RealEstate/Flippers/ProjectTimeline"));
const FlipperContractorBids = lazy(() => import("./pages/RealEstate/Flippers/ContractorBids"));
const FlipperScopeOfWork = lazy(() => import("./pages/RealEstate/Flippers/ScopeOfWork"));
const FlipperMaterialsAndPermits = lazy(() => import("./pages/RealEstate/Flippers/MaterialsAndPermits"));
const FlipperDrawSchedule = lazy(() => import("./pages/RealEstate/Flippers/DrawSchedule"));
const FlipperKPIs = lazy(() => import("./pages/RealEstate/Flippers/FlipKPIs"));
const FlipperProfitLoss = lazy(() => import("./pages/RealEstate/Flippers/FlipperProfitLoss"));
const FlipperGoalTracker = lazy(() => import("./pages/RealEstate/Flippers/FlipperGoalTracker"));
const FlipperPortfolio = lazy(() => import("./pages/RealEstate/Flippers/PortfolioOverview"));
const FlipperLenders = lazy(() => import("./pages/RealEstate/Flippers/LenderManagement"));
const FlipperBRRRR = lazy(() => import("./pages/Flipper/BRRRRCalculator"));
const FlipperDailyLog = lazy(() => import("./pages/Flipper/DailyLog"));
const FlipperInvestorReport = lazy(() => import("./pages/Flipper/InvestorReport"));
const FlipperCompAnalysis = lazy(() => import("./pages/RealEstate/Flippers/CompAnalysis"));
const FlipperContractorScorecard = lazy(() => import("./pages/RealEstate/Flippers/ContractorScorecard"));
const FlipperExitStrategy = lazy(() => import("./pages/RealEstate/Flippers/ExitStrategy"));
const FlipperRehabEstimator = lazy(() => import("./pages/RealEstate/Flippers/FlipperRehabEstimator"));
const FlipperPhotoDocumentation = lazy(() => import("./pages/RealEstate/Flippers/PhotoDocumentation"));

// Learning Module Components
const AppraisalPacketChecklist = lazy(() => import("./components/learning/modules/AppraisalPacketChecklist"));
const FairHousingSafeMarketing = lazy(() => import("./components/learning/modules/FairHousingSafeMarketing"));
const InspectionCredits = lazy(() => import("./components/learning/modules/InspectionCreditsVsRepairs"));
const ListingMedia = lazy(() => import("./components/learning/modules/ListingMediaCopyThatSells"));
const MarketPulse = lazy(() => import("./components/learning/Market"));
const OfferStrategy = lazy(() => import("./components/learning/Offer"));
const PreApprovalPowerUp = lazy(() => import("./components/learning/PreApprovalPowerUp"));
const PricingIn30Minutes = lazy(() => import("./components/learning/PricingIn30Minutes"));
const TransactionTimeline = lazy(() => import("./components/learning/TransactionTimeline"));
const SkipTracingCourse = lazy(() => import("./components/learning/SkipTracingCourse"));
const SocialMediaConverts = lazy(() => import("./components/learning/modules/SocialMediaThatConverts"));
const ReferralSystemBlueprint = lazy(() => import("./components/learning/modules/ReferralSystemBlueprint"));
const LeadNurturingSequences = lazy(() => import("./components/learning/modules/LeadNurturingSequences"));
const ClosingTableMastery = lazy(() => import("./components/learning/modules/ClosingTableMastery"));
const AdvancedObjectionHandling = lazy(() => import("./components/learning/modules/AdvancedObjectionHandling"));
const SOITrainingCourse = lazy(() => import("./components/learning/SOITrainingCourse"));

// Real Estate Featured Learning Courses
const AgentFundamentalsCourse = lazy(() => import("./components/learning/realestate/AgentFundamentalsCourse"));
const ListingMasteryCourse = lazy(() => import("./components/learning/realestate/ListingMasteryCourse"));
const ClientConversionCourse = lazy(() => import("./components/learning/realestate/ClientConversionCourse"));

// Pro Plan Learning Components
const CompetitiveIntelligence = lazy(() => import("./components/learning/modules/CompetitiveIntelligence"));
const CrossSellingRevenue = lazy(() => import("./components/learning/modules/CrossSellingRevenue"));
const DatabaseMining = lazy(() => import("./components/learning/modules/DatabaseMining"));
const HighIncomeClientPsychology = lazy(() => import("./components/learning/modules/HighIncomeClientPsychology"));
const MarketCycleStrategy = lazy(() => import("./components/learning/modules/MarketCycleStrategy"));
const NegotiationMasteryCourse = lazy(() => import("./components/learning/NegotiationMasteryCourse"));

// Flipper Learning Courses
const FlipFundamentalsCourse = lazy(() => import("./components/learning/flipper/FlipFundamentalsCourse"));
const ScalingFlipsCourse = lazy(() => import("./components/learning/flipper/ScalingFlipsCourse"));
const FlipEmpireCourse = lazy(() => import("./components/learning/flipper/FlipEmpireCourse"));

// Probate Learning Courses
const ProbateFundamentalsCourse = lazy(() => import("./components/learning/probate/ProbateFundamentalsCourse"));
const ProbateLeadMasteryCourse = lazy(() => import("./components/learning/probate/ProbateLeadMasteryCourse"));
const ProbateBusinessBuilderCourse = lazy(() => import("./components/learning/probate/ProbateBusinessBuilderCourse"));

// Wholesale Learning Courses
const WholesaleFundamentalsCourse = lazy(() => import("./components/learning/wholesale/WholesaleFundamentalsCourse"));
const AdvancedDealFindingCourse = lazy(() => import("./components/learning/wholesale/AdvancedDealFindingCourse"));
const WholesaleEmpireCourse = lazy(() => import("./components/learning/wholesale/WholesaleEmpireCourse"));

// Add-on Pages
const AddonSocialMediaSuite = lazy(() => import("./pages/RealEstate/Addons/SocialMediaSuite"));
const AddonCMABuilder = lazy(() => import("./pages/RealEstate/Addons/CMABuilder"));
const AddonClientPortalPro = lazy(() => import("./pages/RealEstate/Addons/ClientPortalPro"));
const AddonDirectMailBlaster = lazy(() => import("./pages/RealEstate/Addons/DirectMailBlaster"));
const AddonBuyerMatchmaker = lazy(() => import("./pages/RealEstate/Addons/BuyerMatchmaker"));
const AddonCompsEngine = lazy(() => import("./pages/RealEstate/Addons/CompsEngine"));
const AddonProbateDirectMail = lazy(() => import("./pages/RealEstate/Addons/ProbateDirectMail"));
const AddonCourtMonitor = lazy(() => import("./pages/RealEstate/Addons/CourtMonitor"));
const AddonOfferGenerator = lazy(() => import("./pages/RealEstate/Addons/OfferGenerator"));
const AddonDealFinder = lazy(() => import("./pages/RealEstate/Addons/DealFinder"));
const AddonLenderHub = lazy(() => import("./pages/RealEstate/Addons/LenderHub"));
const AddonListingLaunchpad = lazy(() => import("./pages/RealEstate/Addons/ListingLaunchpad"));
const VendorReviewPage = lazy(() => import("./pages/Public/VendorReviewPage"));
const ClientPortalPage = lazy(() => import("./pages/Public/ClientPortalPage"));
const PublicDealPackage = lazy(() => import("./pages/Public/PublicDealPackage"));
const BuyerDocUpload = lazy(() => import("./pages/Public/BuyerDocUpload"));
const SellerPortalPage = lazy(() => import("./pages/Public/SellerPortalPage"));
const BuyerPortalPage = lazy(() => import("./pages/Public/BuyerPortalPage"));
const InterestConfirmed = lazy(() => import("./pages/Public/InterestConfirmed"));
const NotInterested = lazy(() => import("./pages/Public/NotInterested"));
const PublicCaseTimeline = lazy(() => import("./pages/Public/PublicCaseTimeline"));
const PublicAgentWebsite = lazy(() => import("./pages/Public/PublicAgentWebsite"));
const PublicLandingPage = lazy(() => import("./pages/Public/PublicLandingPage"));
const PublicOpenHouseSignIn = lazy(() => import("./pages/Public/PublicOpenHouseSignIn"));
const AffiliateQuestionnairePage = lazy(() => import("./pages/Public/AffiliateQuestionnairePage"));
const AffiliateAgreementPage = lazy(() => import("./pages/Public/AffiliateAgreementPage"));
const Demo = lazy(() => import("./pages/Demo"));
const DemoLauncher = lazy(() => import("./pages/DemoLauncher"));
const VsHubspot = lazy(() => import("./pages/Compare/VsHubspot"));
const VsFollowUpBoss = lazy(() => import("./pages/Compare/VsFollowUpBoss"));
const VsKvcore = lazy(() => import("./pages/Compare/VsKvcore"));
const PublicNotFound = lazy(() => import("./pages/NotFound"));
const PublicSecurity = lazy(() => import("./pages/Security"));
const PublicAbout = lazy(() => import("./pages/About"));
const PublicRoi = lazy(() => import("./pages/RoiCalculator"));
const PublicCrmFitFinder = lazy(() => import("./pages/CrmFitFinder"));
const PublicSwitchingChecklist = lazy(() => import("./pages/SwitchingChecklist"));
const FeatureRealEstateDrip = lazy(() => import("./pages/Feature/RealEstateDripCampaigns"));
const FeatureRealEstatePipeline = lazy(() => import("./pages/Feature/RealEstatePipeline"));
const FeatureWholesaleBlast = lazy(() => import("./pages/Feature/WholesaleBuyerBlast"));
const FeatureProbateCases = lazy(() => import("./pages/Feature/ProbateCaseManagement"));
const FeatureFlipperBudgets = lazy(() => import("./pages/Feature/FlipperBudgetTracking"));
const MigrateFromHubspot = lazy(() => import("./pages/Migrate/FromHubspot"));
const MigrateFromFub = lazy(() => import("./pages/Migrate/FromFollowUpBoss"));
const MigrateFromKvcore = lazy(() => import("./pages/Migrate/FromKvcore"));
const PublicIntegrations = lazy(() => import("./pages/Integrations"));
const PublicDevelopers = lazy(() => import("./pages/Developers"));
const PublicAffiliateProgram = lazy(() => import("./pages/AffiliateProgram"));
const PublicResources = lazy(() => import("./pages/Resources"));
const PublicArticle = lazy(() => import("./pages/Article"));
const PublicAcceptableUse = lazy(() => import("./pages/AcceptableUse"));

function App() {
  return (
    <Router>
      {/*
        DemoGuardProvider lets any page block a demo visitor's
        write/connect actions ("add a client", "connect Google",
        "save settings") with a shared "just a demo" modal — see
        contexts/DemoGuardContext.jsx. Sits inside Router so the
        modal's "pick a plan" CTA can route, and wraps everything so
        the guard is reachable from every CRM page.
      */}
      <DemoGuardProvider>
      {/*
        TopBanners wraps StagingBanner + DemoBanner in a single
        sticky strip at z-60 so the sidebar (z-50) can no longer
        cover the demo time-remaining chip. A ResizeObserver writes
        the strip's height to the --top-banner-h CSS variable on
        <html>; the sidebar uses that variable to offset its `top`
        so it slides in cleanly below the banners. When neither
        banner is rendering anything the height is 0 and prod
        sessions pay nothing.
      */}
      <TopBanners />
      <Suspense fallback={<FullPageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          {/*
            Marketing-shareable demo routes — public, no auth.
            /demo                       → vertical + tier chooser
            /demo/:vertical             → defaults to Pro tier
            /demo/:vertical/:tier       → exact tier (starter/power/pro)
          */}
          <Route path="/demo" element={<Demo />} />
          <Route path="/demo/:vertical" element={<DemoLauncher />} />
          <Route path="/demo/:vertical/:tier" element={<DemoLauncher />} />
          {/* SEO comparison pages — high-intent search traffic
              ("stacked vs hubspot", "follow up boss alternative"). */}
          <Route path="/vs-hubspot" element={<VsHubspot />} />
          <Route path="/vs-follow-up-boss" element={<VsFollowUpBoss />} />
          <Route path="/vs-kvcore" element={<VsKvcore />} />
          <Route path="/security" element={<PublicSecurity />} />
          <Route path="/about" element={<PublicAbout />} />
          <Route path="/roi-calculator" element={<PublicRoi />} />
          <Route path="/crm-fit-finder" element={<PublicCrmFitFinder />} />
          <Route path="/switching-checklist" element={<PublicSwitchingChecklist />} />
          {/* Feature landing pages — SEO long-tail traffic. */}
          <Route path="/real-estate-drip-campaigns" element={<FeatureRealEstateDrip />} />
          <Route path="/real-estate-pipeline" element={<FeatureRealEstatePipeline />} />
          <Route path="/wholesale-buyer-blast" element={<FeatureWholesaleBlast />} />
          <Route path="/probate-case-management" element={<FeatureProbateCases />} />
          <Route path="/flipper-budget-tracking" element={<FeatureFlipperBudgets />} />
          {/* Migration guides — pair with /vs-* comparison pages */}
          <Route path="/migrate-from-hubspot" element={<MigrateFromHubspot />} />
          <Route path="/migrate-from-follow-up-boss" element={<MigrateFromFub />} />
          <Route path="/migrate-from-kvcore" element={<MigrateFromKvcore />} />
          <Route path="/integrations" element={<PublicIntegrations />} />
          <Route path="/developers" element={<PublicDevelopers />} />
          <Route path="/affiliate-program" element={<PublicAffiliateProgram />} />
          <Route path="/resources" element={<PublicResources />} />
          <Route path="/resources/:slug" element={<PublicArticle />} />
          <Route path="/acceptable-use" element={<PublicAcceptableUse />} />
          <Route path="/login" element={<Login />} />
          {/* /signup → same Login component, opens in signup mode via
              the ?mode=signup query param Login.jsx already reads.
              Gives the marketing surfaces a clean "Sign up" CTA URL
              without standing up a separate page. */}
          <Route path="/signup" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/real-estate" element={<RealEstate />} />
          <Route path="/wholesalers" element={<Wholesalers />} />
          {/*
            IMPORTANT: the public marketing page MUST live at
            /probate-specialists, not /probate.

            The authenticated CRM block below registers /probate as
            the base of the probate CRM dashboard inside
            <ProtectedRoute />. Without auth, ProtectedRoute
            redirects to /login. Registering a public /probate route
            here collides with that CRM base: React Router resolves
            both at the same specificity and the CRM-protected
            version wins, so a logged-out visitor clicking
            "Probate Specialists" in the Use Cases dropdown ended
            up bounced to /login instead of seeing the marketing
            page.

            The other three verticals don't collide because their
            public/CRM URLs differ:
              public /real-estate    vs CRM /realestate
              public /wholesalers    vs CRM /wholesale
              public /home-flippers  vs CRM /flipper
            Only probate had matching slugs.

            Solution: only register /probate-specialists publicly.
            The nav points there. Anyone with an old /probate
            bookmark will still land in the CRM (their normal
            authed path) — that's the expected behavior for
            customers who type the URL after signing in.
          */}
          <Route path="/probate-specialists" element={<Probate />} />
          <Route path="/home-flippers" element={<HomeFlippers />} />
          <Route path="/pricing-summary" element={<PricingSummary />} />
          <Route path="/status" element={<Status />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookies-policy" element={<CookiesPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsandConditionsPolicy />} />
          <Route path="/refund-cancellation-policy" element={<RefundCancellationPolicy />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/sms-opt-out" element={<SMSOptOut />} />
          <Route path="/sms-opt-in" element={<SMSOptIn />} />
          <Route path="/r/:code" element={<Referral />} />
          <Route path="/ref/:code" element={<Referral />} />
          <Route path="/property/:id" element={<RealPropertyDetails />} />
          <Route path="/feedback/:token" element={<PublicFeedbackForm />} />
          <Route path="/vendor-review/:token" element={<VendorReviewPage />} />
          <Route path="/sign/:documentId/:accessToken" element={<ESignPage />} />
          <Route path="/client-portal/:token" element={<ClientPortalPage />} />
          <Route path="/deal-package/:token" element={<PublicDealPackage />} />
          <Route path="/case-timeline/:token" element={<PublicCaseTimeline />} />
          {/* Client transaction timeline. This is both where the in-app
              "View Timeline" button lands AND the link the "Copy Timeline
              Link" button hands to clients, so it lives in the PUBLIC block
              (outside ProtectedRoute): ClientTimeline reads the public
              /clients/public/:id/timeline endpoint, so a client opening the
              shared link doesn't need a login. Previously this path had no
              route at all — it fell through to the catch-all NotFound, which
              then crashed, producing the "Something went wrong" screen. */}
          <Route path="/realestate/clients/:clientId/timeline" element={<RealClientTimeline />} />
          <Route path="/site/:slug" element={<PublicAgentWebsite />} />
          <Route path="/site/:slug/lp/:pageSlug" element={<PublicLandingPage />} />
          <Route path="/upload-docs/:dealId" element={<BuyerDocUpload />} />
          <Route path="/seller-portal/:token" element={<SellerPortalPage />} />
          <Route path="/buyer-portal/:token" element={<BuyerPortalPage />} />
          <Route path="/team-login/:token" element={<TeamLoginPage />} />
          <Route path="/team-portal" element={<TeamPortalPage />} />
          <Route path="/interest-confirmed" element={<InterestConfirmed />} />
          <Route path="/not-interested" element={<NotInterested />} />
          <Route path="/open-house/:token" element={<PublicOpenHouseSignIn />} />
          <Route path="/affiliate-questionnaire/:id" element={<AffiliateQuestionnairePage />} />
          <Route path="/affiliate-agreement/:id" element={<AffiliateAgreementPage />} />
          <Route path="/affiliate" element={<ProtectedRoute type="affiliate" />}>
            <Route path="dashboard" element={<AffiliateDashboard />} />
          </Route>
          <Route path="/payment" element={<PaymentPage />} />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedRoute type="admin" />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="demos" element={<AdminDemos />} />
            <Route path="emails" element={<AdminEmails />} />
            <Route path="texting" element={<AdminTexting />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="affiliates" element={<AdminAffiliates />} />
            <Route path="news-letters" element={<AdminNewsLetters />} />
            <Route path="calendar" element={<AdminCalendar />} />
            <Route path="account-look-up" element={<AccountLookUp />} />
            <Route path="activities" element={<AdminActivities />} />
            <Route path="tickets" element={<AdminTickets />} />
            <Route path="goals" element={<AdminGoals />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="follow-ups" element={<AdminFollowUps />} />
            <Route path="tasks" element={<AdminTasks />} />
            <Route path="user-management" element={<AdminUserManagement />} />
            <Route path="business-insights" element={<AdminBusinessInsights />} />
            <Route path="audit-log" element={<AdminAuditLog />} />
            <Route path="suspicious-activity" element={<AdminSuspiciousActivity />} />
            <Route path="security" element={<AdminSecurity />} />
            <Route path="list-formatting" element={<AdminListFormatting />} />
            <Route path="broadcast-email" element={<AdminBroadcastEmail />} />
            <Route path="webhook-events" element={<AdminWebhookEvents />} />
          </Route>

          {/* CRM Routes - Each CRM type gets its own base URL */}
          {/* CASA Tier 2: All CRM routes require authentication */}
          {['realestate', 'wholesale', 'probate', 'flipper'].map((crmSlug) => (
            <Route key={crmSlug} path={`/${crmSlug}`} element={<ProtectedRoute />}>
            <Route element={<RealDashboard />}>
            <Route index element={<DashboardRouter />} />
            <Route path="dashboard" element={<DashboardRouter />} />
            <Route path="clients" element={<RealCients />} />
            <Route path="clients/:clientId" element={<RealClientDetail />} />
            <Route path="properties" element={<RealProperties />} />
            <Route path="properties/:id" element={<RealPropertyDetails />} />
            <Route path="calendar" element={<RealCalendar />} />
            <Route path="tasks" element={<RealTaskManagement />} />
            <Route path="transactions" element={<RealTransactionManagement />} />
            <Route path="listing-lifecycle" element={<RealListingLifecycle />} />
            <Route path="my-listings" element={<RealMyListings />} />
            <Route path="google-contacts" element={<RealGoogleContactsSync />} />
            <Route path="connected-accounts" element={<RealConnectedAccounts />} />
            <Route path="communications" element={<RealCommunicationTracking />} />
            <Route path="documents" element={<RealDocument />} />
            <Route path="analytics" element={<RealAnalytics />} />
            <Route path="library" element={<RealLibrary />} />
            
            {/* Wholesale CRM Routes */}
            <Route path="sellers" element={<WholesaleSellers />} />
            <Route path="buyers" element={<WholesaleBuyers />} />
            <Route path="deals" element={<WholesaleDeals />} />
            <Route path="list-stacking" element={<WholesaleListStacking />} />
            <Route path="seller-pipeline" element={<WholesaleSellerPipeline />} />
            <Route path="buyer-blast" element={<WholesaleBuyerBlast />} />
            <Route path="driving-for-dollars" element={<WholesaleDrivingForDollars />} />
            <Route path="offer-calculator" element={<WholesaleOfferCalculator />} />
            
            {/* Learning Routes */}
            <Route path="learning" element={<RealNewLearning />} />
            <Route path="learning/appraisal-packet-checklist" element={<AppraisalPacketChecklist />} />
            <Route path="learning/fair-housing-safe-marketing" element={<FairHousingSafeMarketing />} />
            <Route path="learning/inspection-credits-vs-repairs" element={<InspectionCredits />} />
            <Route path="learning/listing-media-copy-that-sells" element={<ListingMedia />} />
            <Route path="learning/market-pulse-in-5-minutes" element={<MarketPulse />} />
            <Route path="learning/offer-strategy-win-without-overpaying" element={<OfferStrategy />} />
            <Route path="learning/pre-approval-power-up" element={<PreApprovalPowerUp />} />
            <Route path="learning/pricing-a-listing-in-30-minutes" element={<PricingIn30Minutes />} />
            <Route path="learning/transaction-timeline" element={<TransactionTimeline />} />
            <Route path="learning/skip-tracing-course" element={<SkipTracingCourse />} />
            <Route path="learning/social-media-that-converts" element={<SocialMediaConverts />} />
            <Route path="learning/referral-system-blueprint" element={<ReferralSystemBlueprint />} />
            <Route path="learning/lead-nurturing-sequences" element={<LeadNurturingSequences />} />
            <Route path="learning/closing-table-mastery" element={<ClosingTableMastery />} />
            <Route path="learning/advanced-objection-handling" element={<AdvancedObjectionHandling />} />
            <Route path="learning/soi-training-course" element={<SOITrainingCourse />} />
            
            {/* Real Estate Featured Learning Course Routes */}
            <Route path="learning/agent-fundamentals-course" element={<AgentFundamentalsCourse />} />
            <Route path="learning/listing-mastery-course" element={<ListingMasteryCourse />} />
            <Route path="learning/client-conversion-course" element={<ClientConversionCourse />} />
            
            {/* Pro Plan Learning Routes */}
            <Route path="learning/competitive-intelligence" element={<CompetitiveIntelligence />} />
            <Route path="learning/cross-selling-revenue" element={<CrossSellingRevenue />} />
            <Route path="learning/database-mining" element={<DatabaseMining />} />
            <Route path="learning/high-income-psychology" element={<HighIncomeClientPsychology />} />
            <Route path="learning/market-cycle-strategy" element={<MarketCycleStrategy />} />
            <Route path="learning/negotiation-mastery-course" element={<NegotiationMasteryCourse />} />
            
            {/* Flipper Learning Course Routes */}
            <Route path="learning/flip-fundamentals-course" element={<FlipFundamentalsCourse />} />
            <Route path="learning/scaling-flips-course" element={<ScalingFlipsCourse />} />
            <Route path="learning/flip-empire-course" element={<FlipEmpireCourse />} />
            
            {/* Probate Learning Course Routes */}
            <Route path="learning/probate-fundamentals-course" element={<ProbateFundamentalsCourse />} />
            <Route path="learning/probate-lead-mastery-course" element={<ProbateLeadMasteryCourse />} />
            <Route path="learning/probate-business-builder-course" element={<ProbateBusinessBuilderCourse />} />
            
            {/* Wholesale Learning Course Routes */}
            <Route path="learning/wholesale-fundamentals-course" element={<WholesaleFundamentalsCourse />} />
            <Route path="learning/advanced-deal-finding-course" element={<AdvancedDealFindingCourse />} />
            <Route path="learning/wholesale-empire-course" element={<WholesaleEmpireCourse />} />
            
            {/* Legacy URL kept for bookmarks. */}
            <Route path="new-learning" element={<Navigate to="../learning" replace />} />
            <Route path="settings" element={<RealSettings />} />
            {/* This mounted the Admin security page inside a customer's CRM,
                duplicating the two-factor section Settings already owns. Nothing
                linked it. */}
            <Route path="security" element={<Navigate to="../settings#section-two-factor" replace />} />
            <Route path="help-center" element={<RealHelpCenter />} />
            <Route path="messaging" element={<RealMessaging />} />
            <Route path="pricing" element={<RealPricing />} />
            <Route path="marketing-tools/*" element={<RealMarketingTools />} />
            
            {/* Add-on Routes */}
            <Route path="addon/social-media-suite" element={<AddonGate addonKey="agentSocialMediaSuite"><AddonSocialMediaSuite /></AddonGate>} />
            <Route path="addon/cma-builder" element={<AddonGate addonKey="agentCMABuilder"><AddonCMABuilder /></AddonGate>} />
            <Route path="addon/client-portal" element={<AddonGate addonKey="agentClientPortalPro"><AddonClientPortalPro /></AddonGate>} />
            <Route path="addon/direct-mail-blaster" element={<AddonGate addonKey="wholesaleDirectMailBlaster"><AddonDirectMailBlaster /></AddonGate>} />
            <Route path="addon/buyer-matchmaker" element={<AddonGate addonKey="wholesaleBuyerMatchmaker"><AddonBuyerMatchmaker /></AddonGate>} />
            <Route path="addon/comps-engine" element={<AddonGate addonKey="wholesaleCompsEngine"><AddonCompsEngine /></AddonGate>} />
            <Route path="addon/probate-direct-mail" element={<AddonGate addonKey="probateDirectMailEngine"><AddonProbateDirectMail /></AddonGate>} />
            <Route path="addon/court-monitor" element={<AddonGate addonKey="probateCourtMonitor"><AddonCourtMonitor /></AddonGate>} />
            <Route path="addon/offer-generator" element={<AddonGate addonKey="probateOfferGenerator"><AddonOfferGenerator /></AddonGate>} />
            <Route path="addon/deal-finder" element={<AddonGate addonKey="flipperDealFinder"><AddonDealFinder /></AddonGate>} />
            <Route path="addon/lender-hub" element={<AddonGate addonKey="flipperLenderConnect"><AddonLenderHub /></AddonGate>} />
            <Route path="addon/listing-launchpad" element={<AddonGate addonKey="flipperListingLaunchpad"><AddonListingLaunchpad /></AddonGate>} />
            <Route path="open-house-toolkit" element={<RealOpenHouseToolkit />} />
            <Route path="website-builder" element={<WebsiteBuilder />} />
            <Route path="pipeline" element={<RealPipeline />} />
            {/* Legacy URL. Nothing links here, and the page it rendered read
                from a different source than the live one — it showed "0 Active
                Campaigns" to users who had campaigns running on /auto-drip.
                Redirect rather than delete so old bookmarks still land somewhere
                true. */}
            <Route path="drip-campaign" element={<Navigate to="../auto-drip" replace />} />
            {/* Earlier version of Contact Import — same three source options,
                without the column mapping — that nothing linked to. */}
            <Route path="client-upload" element={<Navigate to="../client-import" replace />} />
            <Route path="client-import" element={<RealClientImport />} />
            <Route path="crm-import" element={<CRMImportWizard />} />
            <Route path="crm-import/history" element={<CRMImportHistory />} />
            <Route path="email-signature" element={<EmailSignatureBuilder />} />
            <Route path="csv-import" element={<CSVImporter />} />
            <Route path="client-engagement" element={<RealClientEngagement />} />
            {/* Team management merged into Lead Routing — redirect old links */}
            <Route path="team-collaboration" element={<Navigate to="../lead-routing" replace />} />
            <Route path="quick-referral-tool" element={<RealQuickReferral />} />
            <Route path="deal-analyzer" element={<RealDealAnalyzer />} />
            <Route path="neighborhood-farms" element={<RealNeighborhoodFarms />} />
            <Route path="soi-tracker" element={<RealSOITracker />} />
            <Route path="showing-feedback" element={<RealShowingFeedback />} />
            <Route path="follow-up-sequences" element={<RealFollowUpSequences />} />
            <Route path="vendor-scorecard" element={<RealVendorScorecard />} />
            <Route path="voice-calls" element={<RealVoiceCalls />} />
            <Route path="basic-dialer" element={<RealBasicDialer />} />
            <Route path="email-inbox" element={<RealEmailInbox />} />
            <Route path="email-templates" element={<RealEmailTemplateGallery />} />
            <Route path="favorites" element={<RealMyFavorites />} />
            
            {/* Power Plan Routes */}
            <Route path="e-signature" element={<RealESignatureHub />} />
            <Route path="lead-capture" element={<RealLeadCapture />} />
            <Route path="lead-inbox" element={<RealLeadInbox />} />
            <Route path="lead-scoring" element={<RealLeadScoring />} />
            <Route path="speed-to-lead" element={<RealSpeedToLead />} />
            <Route path="bulk-send" element={<RealBulkSend />} />
            <Route path="lead-routing" element={<RealLeadRouting />} />
            <Route path="mortgage-calculator" element={<RealMortgageCalc />} />
            <Route path="seller-net-sheet" element={<RealSellerNetSheet />} />
            <Route path="affordability-calculator" element={<RealAffordabilityCalc />} />
            <Route path="qr-code-generator" element={<RealQRCodeGen />} />
            <Route path="mortgage-rates" element={<RealMortgageRates />} />
            <Route path="marketing-automation" element={<RealMarketingDashboard />} />
            <Route path="auto-drip" element={<RealAutoDripCampaigns />} />
            <Route path="commission-tracker" element={<RealCommissionTracker />} />
            <Route path="review-requests" element={<RealReviewRequests />} />
            <Route path="client-anniversaries" element={<RealClientAnniversaries />} />
            <Route path="vendor-directory" element={<RealVendorDirectory />} />
            
            {/* New Real Estate Starter Features */}
            <Route path="listing-checklist" element={<RealListingChecklist />} />
            <Route path="buyer-preferences" element={<RealBuyerPreferences />} />
            
            {/* New Real Estate Power Features */}
            <Route path="closing-countdown" element={<RealClosingCountdown />} />
            <Route path="showing-scheduler" element={<RealShowingScheduler />} />
            
            {/* New Real Estate Pro Features */}
            <Route path="agent-kpis" element={<RealAgentKPIDashboard />} />
            <Route path="profit-loss-tracker" element={<RealProfitLossTracker />} />
            <Route path="goal-tracker" element={<RealGoalTracker />} />
            
            {/* Wholesale Power Plan Routes */}
            <Route path="deal-tracker" element={<WholesaleDealTracker />} />
            <Route path="buyer-followup" element={<WholesaleBuyerFollowup />} />
            <Route path="dispo-checklist" element={<WholesaleDispoChecklist />} />
            <Route path="skip-trace-manager" element={<WholesaleSkipTraceManager />} />
            <Route path="contracts-manager" element={<WholesaleContractsManager />} />
            <Route path="wholesale-e-signature" element={<WholesaleESignatureHub />} />
            <Route path="wholesale-goals" element={<WholesaleGoalTracker />} />
            
            {/* Wholesale Pro Plan Routes */}
            <Route path="kpi-dashboard" element={<WholesaleKPIDashboard />} />
            <Route path="profit-loss" element={<WholesaleProfitLossTracker />} />
            <Route path="va-tasks" element={<WholesaleVATasks />} />
            <Route path="investor-portal" element={<WholesaleInvestorPortal />} />
            <Route path="team-management" element={<WholesaleTeamManagement />} />
            <Route path="dispo-management" element={<WholesaleDispoManagement />} />
            <Route path="wholesale-analytics" element={<WholesaleAnalytics />} />
            <Route path="probate-analytics" element={<ProbateAnalytics />} />
            {/* Same page as /buyers. Two URLs for one page meant the sidebar
                highlighted nothing when you arrived from Client Engagement. */}
            <Route path="buyer-list" element={<Navigate to="../buyers" replace />} />
            
            {/* Wholesale Starter Routes */}
            <Route path="rehab-estimator" element={<WholesaleRehabEstimator />} />
            
            {/* Probate CRM Routes */}
            <Route path="probate-cases" element={<ProbateCases />} />
            <Route path="probate-heirs" element={<ProbateHeirs />} />
            <Route path="probate-court-calendar" element={<ProbateCourtCalendar />} />
            <Route path="probate-case-checklist" element={<ProbateCaseChecklist />} />
            <Route path="probate-heir-finder" element={<ProbateHeirFinder />} />
            <Route path="probate-case-timeline" element={<ProbateCaseTimeline />} />
            <Route path="probate-court-filings" element={<ProbateCourtFilingTracker />} />
            <Route path="probate-attorney-network" element={<ProbateAttorneyNetwork />} />
            <Route path="probate-estate-valuation" element={<ProbateEstateValuation />} />
            <Route path="probate-heir-outreach" element={<Navigate to="../probate-heir-finder" replace />} />
            <Route path="probate-skip-trace" element={<ProbateSkipTrace />} />
            <Route path="probate-pipeline" element={<ProbatePipeline />} />
            <Route path="probate-letter-templates" element={<ProbateLetterTemplates />} />
            <Route path="probate-asset-inventory" element={<EstateAssetInventory />} />
            <Route path="probate-milestones" element={<ProbateMilestones />} />
            <Route path="probate-kpis" element={<ProbateKPIs />} />
            <Route path="probate-profit-loss" element={<ProbateProfitLoss />} />
            <Route path="probate-goals" element={<ProbateGoalTracker />} />
            <Route path="probate-county-analytics" element={<ProbateCountyAnalytics />} />
            <Route path="probate-referral-network" element={<ProbateReferralNetwork />} />
            {/* Same component as probate-asset-inventory, mounted under a second
                name — two URLs rendering one page. */}
            <Route path="probate-estate-assets" element={<Navigate to="../probate-asset-inventory" replace />} />
            <Route path="flipper-analytics" element={<FlipperAnalytics />} />
            <Route path="probate-properties" element={<RealProperties />} />
            
            {/* Flipper CRM Routes */}
            <Route path="flipper-projects" element={<FlipperProjects />} />
            <Route path="flipper-contractors" element={<FlipperContractors />} />
            <Route path="flipper-budget" element={<FlipperBudgetTracker />} />
            <Route path="flipper-holding-costs" element={<FlipperHoldingCosts />} />
            <Route path="flipper-arv-calculator" element={<FlipperARVCalculator />} />
            <Route path="flipper-offer-calculator" element={<FlipperOfferCalculator />} />
            <Route path="flipper-project-timeline" element={<FlipperProjectTimeline />} />
            <Route path="flipper-contractor-bids" element={<FlipperContractorBids />} />
            <Route path="flipper-scope-of-work" element={<FlipperScopeOfWork />} />
            <Route path="flipper-materials" element={<FlipperMaterialsAndPermits />} />
            <Route path="flipper-draw-schedule" element={<FlipperDrawSchedule />} />
            <Route path="flipper-kpis" element={<FlipperKPIs />} />
            <Route path="flipper-profit-loss" element={<FlipperProfitLoss />} />
            <Route path="flipper-goals" element={<FlipperGoalTracker />} />
            <Route path="flipper-portfolio" element={<FlipperPortfolio />} />
            <Route path="flipper-lenders" element={<FlipperLenders />} />
            <Route path="flipper-brrrr" element={<FlipperBRRRR />} />
            <Route path="flipper-daily-log" element={<FlipperDailyLog />} />
            <Route path="flipper-investor-report" element={<FlipperInvestorReport />} />
            <Route path="flipper-pipeline" element={<FlipPipeline />} />
            <Route path="flipper-punch-list" element={<RehabPunchList />} />
            <Route path="flipper-profit-calculator" element={<FlipProfitCalc />} />
            <Route path="flipper-rental-roi" element={<RentalROICalc />} />
            <Route path="flipper-comps" element={<FlipperCompAnalysis />} />
            <Route path="flipper-contractor-scorecard" element={<FlipperContractorScorecard />} />
            <Route path="flipper-exit-strategy" element={<FlipperExitStrategy />} />
            <Route path="flipper-rehab-estimator" element={<FlipperRehabEstimator />} />
            <Route path="flipper-photos" element={<FlipperPhotoDocumentation />} />
            <Route path="flipper-properties" element={<RealProperties />} />
            
            <Route path="*" element={<RealPageNotFound />} />
          </Route>
          </Route>
          ))}

          {/* Client Timeline & Status — now require auth (available inside CRM routes above) */}
          {/* Public timeline access uses the /client-portal/:token or /case-timeline/:token routes instead */}

          {/* Public 404 — branded, recovers visitor with demo CTA + nav */}
          <Route path="*" element={<PublicNotFound />} />
        </Routes>
        <GlobalChatbot />
        <CookieConsentBanner />
      </Suspense>
      </DemoGuardProvider>
    </Router>
  );
}

export default App;
